/**
 * Frontend WebSocket client abstraction for the Inbox module.
 *
 * Responsibilities:
 *   - Connects to the FastAPI WebSocket endpoint with the user's JWT token.
 *   - Delivers parsed event objects to registered callbacks.
 *   - Auto-reconnects with exponential back-off (max 30 s) on unintended disconnects.
 *   - Exposes a clean close() method to stop reconnecting intentionally
 *     (e.g. when the user navigates away from the Inbox page).
 *
 * Usage (inside useInbox.ts):
 *   const ws = createInboxWebSocket(token, {
 *     onNewMessage: (msg) => setMessages(prev => [...prev, msg]),
 *     onConversationUpdated: (conv) => updateConversation(conv),
 *   });
 *   // cleanup:
 *   return () => ws.close();
 */

import { InboxMessage, ChatConversation } from '../domain/types';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
// Convert http(s):// → ws(s)://
const WS_BASE = BACKEND_URL.replace(/^http/, 'ws');

export interface InboxSocketCallbacks {
  onNewMessage?: (message: InboxMessage) => void;
  onConversationUpdated?: (conversation: Partial<ChatConversation> & { phone_number: string }) => void;
  onMessageStatusUpdated?: (update: {
    _id: string;
    phone_number?: string;
    message_id?: string;
    status?: string;
    delivery_status: string;
  }) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export interface InboxWebSocketHandle {
  /** Intentionally close the socket and stop auto-reconnect. */
  close: () => void;
}

const MAX_RECONNECT_DELAY_MS = 30_000;

export function createInboxWebSocket(
  token: string,
  callbacks: InboxSocketCallbacks
): InboxWebSocketHandle {
  let ws: WebSocket | null = null;
  let intentionallyClosed = false;
  let reconnectDelay = 1_000; // start at 1 s

  function connect() {
    if (intentionallyClosed) return;

    const url = `${WS_BASE}/api/inbox/ws?token=${encodeURIComponent(token)}`;
    ws = new WebSocket(url);

    ws.onopen = () => {
      reconnectDelay = 1_000; // reset back-off on successful connect
      callbacks.onConnected?.();
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data as string);
        const eventType: string = payload.event;

        if (eventType === 'NEW_MESSAGE') {
          callbacks.onNewMessage?.(payload as InboxMessage);
        } else if (eventType === 'CONVERSATION_UPDATED') {
          callbacks.onConversationUpdated?.(payload);
        } else if (eventType === 'MESSAGE_STATUS_UPDATED') {
          callbacks.onMessageStatusUpdated?.(payload);
        }
      } catch {
        // Malformed JSON — ignore silently
      }
    };

    ws.onclose = (ev: CloseEvent) => {
      callbacks.onDisconnected?.();
      if (!intentionallyClosed && ev.code !== 1008 /* policy violation = bad token */) {
        // Exponential back-off (capped at 30 s)
        const delay = reconnectDelay;
        reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS);
        setTimeout(connect, delay);
      }
    };

    ws.onerror = () => {
      // onclose fires after onerror — reconnect logic lives there
      ws?.close();
    };
  }

  connect();

  return {
    close() {
      intentionallyClosed = true;
      ws?.close();
    },
  };
}
